import { memo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Col, Form, Input, Row, Space } from 'antd';
import { HomeSvg } from '@/assets/icons';
import { map } from 'lodash';
import {
  useGetAdoptionLiaisonsFamilyQuery,
  useGetCountiesQuery,
  useGetCriteriaTypesQuery,
  useGetHomeStudyStatusQuery,
  useGetHomeStudyTypesQuery,
} from '@/services/general-api';
import { useSearchParentsMutation } from '@/services/parent-api';
import { BanSvg, SearchSvg } from '@/assets/icons';
import CollapsePanel from '@/components/collapse-panel';
import Select from '@/components/select';
import Button from '@/components/button';

const ActiveOrNot = [
  { id: true, name: 'Active' },
  { id: false, name: 'Inactive' },
  { id: null, name: 'Both Active & Inactive' },
];

const YesNoOptions = [
  { id: true, name: 'Yes' },
  { id: false, name: 'No' },
];

const recommendationOptions = [
  { id: 0, name: 'No'},
  { id: 2, name: 'No - Licensed Adopt-Only, 0-5'},
  { id: 1, name: 'Yes - All'},
  { id: 23457, name: 'Yes - Licensed Foster Parent'},
  { id: 23467, name: 'Yes - Licensed to Adopt Only'},
  { id: 23441, name: 'Yes - Adopt-Only Legacy'}
];

const SearchPanel = ({ params }) => {
  const navigate = useNavigate();
  const { data: criteriaData, isLoading: loadingCriteriaData } = useGetCriteriaTypesQuery();
  const { data: liaisonData, isLoading: loadingLiaisonData } = useGetAdoptionLiaisonsFamilyQuery();
  const { data: countryData, isLoading: loadingCountryData } = useGetCountiesQuery();
  const { data: statusData, isLoading: loadingStatusData } = useGetHomeStudyStatusQuery();
  const { data: typeData, isLoading: loadingTypeData } = useGetHomeStudyTypesQuery();
  const [searchParents, { isLoading: loadingSearch }] = useSearchParentsMutation();
  const [form] = Form.useForm();
  const refData = useRef({});

  useEffect(() => {
    if (params) {
      form.setFieldsValue({
        ...params,
        adoptionLiaisonsIds: map(params.adoptionLiaisonsIds, 'id'),
        countyIds: map(params.countyIds, 'id'),
        homeStudy: {
          statusIds: map(params.homeStudy.statusIds, 'id'),
          typeIds: map(params.homeStudy.typeIds, 'id'),
        },
      });
    }
  }, [form, params]);

  useEffect(() => {
    if (params && refData.current.submitted) {
      refData.current.submitted = false;
      document.getElementById('search-results').scrollIntoView({ behavior: 'smooth' });
    }
  }, [params]);

  const onFinish = async (values) => {
    values.adoptionLiaisonsIds = values.adoptionLiaisonsIds?.map((id) => ({ id }));
    values.countyIds = values.countyIds?.map((id) => ({ id }));
    values.homeStudy.statusIds = values.homeStudy.statusIds?.map((id) => ({ id }));
    values.homeStudy.typeIds = values.homeStudy.typeIds?.map((id) => ({ id }));

    try {
      const searchId = await searchParents(values).unwrap();
      navigate(`.?q=${searchId}`);
      refData.current.submitted = true;
    } catch (err) {}
  };

  const onReset = () => {
    form.resetFields();
    navigate('.');
  };

  return (
    <Form
      layout="vertical"
      form={form}
      initialValues={{
        name: {
          first: { criteria: 'Contains' },
          last: { criteria: 'Contains' },
        },
        email: { criteria: 'Contains' },
      }}
      onFinish={onFinish}
    >
      <CollapsePanel icon={<SearchSvg />} title="Parent Search">
        <Row gutter={24}>
          <Col xs={24} md={12}>
            <Form.Item label="First Name">
              <Space.Compact style={{ width: '100%' }}>
                <Form.Item name={['name', 'first', 'criteria']} noStyle>
                  <Select
                    valueName="name"
                    options={criteriaData}
                    loading={loadingCriteriaData}
                    style={{ maxWidth: 125 }}
                  />
                </Form.Item>
                <Form.Item name={['name', 'first', 'value']} noStyle>
                  <Input />
                </Form.Item>
              </Space.Compact>
            </Form.Item>
            <Form.Item label="Last Name">
              <Space.Compact style={{ width: '100%' }}>
                <Form.Item name={['name', 'last', 'criteria']} noStyle>
                  <Select
                    valueName="name"
                    options={criteriaData}
                    loading={loadingCriteriaData}
                    style={{ maxWidth: 125 }}
                  />
                </Form.Item>
                <Form.Item name={['name', 'last', 'value']} noStyle>
                  <Input />
                </Form.Item>
              </Space.Compact>
            </Form.Item>
            <Form.Item label="Email">
              <Space.Compact style={{ width: '100%' }}>
                <Form.Item name={['email', 'criteria']} noStyle>
                  <Select
                    valueName="name"
                    options={criteriaData}
                    loading={loadingCriteriaData}
                    style={{ maxWidth: 125 }}
                  />
                </Form.Item>
                <Form.Item name={['email', 'value']} noStyle>
                  <Input />
                </Form.Item>
              </Space.Compact>
            </Form.Item>
            <Form.Item name="activeStatus" label="Include" initialValue="Active">
              <Select options={ActiveOrNot} allowClear />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="adoptionLiaisonsIds" label="Adoption Liaison">
              <Select mode="multiple" options={liaisonData} loading={loadingLiaisonData} />
            </Form.Item>
            <Row gutter={24}>
              <Col xs={24} md={12}>
                <Form.Item name="countyIds" label="County">
                  <Select
                    mode="multiple"
                    options={countryData}
                    loading={loadingCountryData}
                    filterOption={(input, option) => option.name.toLowerCase().startsWith(input.toLowerCase())}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="recommendationTypeId" label="Recommended">
                  <Select options={recommendationOptions} allowClear placeholder="Select" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={24}>
              <Col>
                <div>
                  <h2>
                    <HomeSvg />
                    &nbsp;Family Status
                  </h2>
                </div>
              </Col>
            </Row>
            <Row gutter={24}>
              <Col xs={24} md={12}>
                <Form.Item name={['homeStudy', 'statusIds']} label="Status">
                  <Select mode="multiple" options={statusData} loading={loadingStatusData} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name={['homeStudy', 'typeIds']} label="Type">
                  <Select mode="multiple" options={typeData} loading={loadingTypeData} />
                </Form.Item>
              </Col>
            </Row>
          </Col>
        </Row>

        <div style={{ display: 'flex', justifyContent: 'end', gap: 16 }}>
          <Button icon={<BanSvg />} onClick={onReset} disabled={loadingSearch}>
            Reset
          </Button>
          <Button type="primary" htmlType="submit" icon={<SearchSvg />} loading={loadingSearch}>
            Search
          </Button>
        </div>
      </CollapsePanel>
    </Form>
  );
};

export default memo(SearchPanel);
